import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Zap, Truck } from "lucide-react";

const SKIP_PATHS = ["/select-role", "/auth/callback", "/payment/callback", "/payment/success", "/payment/cancel"];

export default function DisclaimerModal() {
  const { user, refresh } = useAuth();
  const loc = useLocation();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  const show =
    user && user !== false && !user.disclaimer_ack && !user.needs_role_selection &&
    !hidden && !SKIP_PATHS.some((p) => loc.pathname.startsWith(p));

  if (!show) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/auth/acknowledge-disclaimer");
      setHidden(true);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" data-testid="disclaimer-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg rounded-xl border border-white/10 bg-[#121212] p-8 shadow-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF9F0A]/40 bg-[#FF9F0A]/10">
          <ShieldAlert className="h-3.5 w-3.5 text-[#FF9F0A]" />
          <span className="text-[10px] font-semibold text-[#FF9F0A] uppercase tracking-widest">One-time notice</span>
        </div>
        <h2 className="mt-4 font-black tracking-tight text-2xl text-white">A quick, important note</h2>
        <div className="mt-4 space-y-3 text-sm text-neutral-400 leading-relaxed">
          <p className="flex items-start gap-2.5">
            <Zap className="h-4 w-4 text-[#007AFF] flex-shrink-0 mt-0.5" />
            <span>
              Vismarks is a <span className="text-white font-semibold">powerful visibility engine</span> — every
              product goes live in front of AI-matched buyers and stays visible until you remove it. It is{" "}
              <span className="text-white font-semibold">not a promise or guarantee of sales</span>. Results
              depend on demand, pricing, and product quality.
            </span>
          </p>
          <p className="flex items-start gap-2.5">
            <Truck className="h-4 w-4 text-[#007AFF] flex-shrink-0 mt-0.5" />
            <span>
              Sellers handle all shipping, delivery, and returns — Vismarks is not a logistics provider.
            </span>
          </p>
        </div>
        <Link to="/legal" data-testid="disclaimer-modal-legal-link" className="inline-block mt-4 text-xs text-[#007AFF] hover:underline">
          Read the full Legal Notice →
        </Link>
        <Button
          onClick={accept}
          disabled={busy}
          data-testid="disclaimer-modal-accept-btn"
          className="mt-6 w-full bg-white text-black hover:bg-neutral-200 rounded-full h-11 font-semibold"
        >
          {busy ? "Saving…" : "I understand & agree"}
        </Button>
      </div>
    </div>
  );
}
